import { useStytchB2BClient } from '@stytch/react/b2b';
import { MemberSession } from '@stytch/vanilla-js';
import { useEffect, useRef } from 'react';
import log from 'debug';
import config from '../config';

const debug = log('session-refresh');

const sessionDurationMinutes = parseInt(config.sessionDurationMinutes);
const bufferMinutes = parseInt(config.sessionBufferMinutes);
const activityTimeoutMinutes = parseInt(config.sessionActivityTimeoutMinutes);
const maxSessionDurationMinutes = parseInt(config.sessionMaxDurationMinutes);

const minutesToMs = (minutes: number) => minutes * 60 * 1000;
const msToMinutes = (milliseconds: number) => Math.ceil(milliseconds / 1000 / 60);

const refreshInterval = minutesToMs(sessionDurationMinutes) - minutesToMs(bufferMinutes);

const getNextSessionDurationMinutes = (session: MemberSession) => {
    const elapsed = Date.now() - Date.parse(session.started_at);
    const max = minutesToMs(maxSessionDurationMinutes);

    debug('elapsed', elapsed, msToMinutes(elapsed));
    debug('max', max, maxSessionDurationMinutes);

    if (elapsed >= max) {
        debug('elapsed greater than max')
        return;
    }

    // stytch enforces a minimum of 5 minutes
    if (msToMinutes(max - elapsed) < 5) {
        debug('next session less than 5 minutes')
        return;
    }

    return elapsed + refreshInterval > max
        ? msToMinutes(max - elapsed)
        : sessionDurationMinutes;
}

export default function useSessionRefresh(useMouseActivity = false) {
    const stytch = useStytchB2BClient();

    const hasActivityRef = useRef(true);

    useEffect(() => {
        const session = stytch.session.getSync();

        if (session && Date.parse(session.expires_at) - Date.now() < refreshInterval) {
            // Session would expire before refresh interval triggers. Refresh now and then start the interval
            const nextSessionDurationMinutes = getNextSessionDurationMinutes(session);
            debug('fresh window next session duration', nextSessionDurationMinutes);
            void stytch.session.authenticate({
                session_duration_minutes: nextSessionDurationMinutes,
            })
        }

        const refresh = () => {
            const currentSession = stytch.session.getSync();

            if (currentSession && (hasActivityRef.current || !useMouseActivity)) {
                const nextSessionDurationMinutes = getNextSessionDurationMinutes(currentSession);
                debug('refresh function next session duration', nextSessionDurationMinutes);

                void stytch.session.authenticate({
                    session_duration_minutes: nextSessionDurationMinutes,
                });
            }
        };

        const interval = setInterval(refresh, refreshInterval);
        return () => clearInterval(interval);
    }, [stytch, useMouseActivity]);

    const timeOutFuncRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!useMouseActivity) return;

        const timeoutFunc = () => {
            if (timeOutFuncRef.current) {
                clearTimeout(timeOutFuncRef.current);
            }
            timeOutFuncRef.current = setTimeout(() => hasActivityRef.current = false, minutesToMs(activityTimeoutMinutes));
        }

        addEventListener('mousemove', timeoutFunc);
        return  () => removeEventListener('mousemove', timeoutFunc);
    }, [useMouseActivity]);
}